import React from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft,
  ArrowRight,
  Clock,
  Sparkles,
  AlertTriangle,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/Breadcrumbs';

/**
 * Long-tail wedge piece targeting Hazel users searching for things like
 * "Hazel content matching PDF" or "Hazel PDF rules not working". The post is
 * built as its own page (rather than a CMS-backed article) so it ships with
 * the rest of the static landing site and gets stamped with its own meta in
 * the route-meta pipeline.
 */

const PUBLISHED = '2026-05-14';
const CANONICAL = 'https://www.getsortio.com/blog/hazel-content-matching-pdfs';

const FAQ = [
  {
    question: 'Why does Hazel content matching fail on some PDFs but work on others?',
    answer:
      "It is almost always the underlying OCR text layer. Hazel does not read the visual page; it reads the text the PDF's OCR layer exposes. When the source bank scans on a slightly different machine, the OCR engine introduces tiny differences (extra whitespace, transposed characters, broken ligatures) that the Hazel regex does not anticipate. The fix is to either rebuild the OCR layer with a more permissive engine or move the routing decision out of regex entirely.",
  },
  {
    question: 'Can I use OCRmyPDF to fix the OCR layer before Hazel reads it?',
    answer:
      "Yes, and many heavy Hazel users do exactly that. The pattern is: a Hazel rule that detects a new PDF and runs OCRmyPDF against it, then a second rule that does the content matching once the canonical OCR layer is in place. It works, but it adds a brittle build step, and it does not help with PDFs that have correct OCR but inconsistent layout.",
  },
  {
    question: 'Does Sortio replace Hazel for PDF content matching?',
    answer:
      "For the cases where Hazel content matching is brittle, yes. Sortio uses an LLM to read PDF content, which is robust to OCR noise in a way regex is not. For deterministic patterns where Hazel already works, you do not need to replace anything. The pragmatic workflow is to use rules for the clean cases and Sortio for the messy ones.",
  },
  {
    question: 'Does Sortio process PDFs locally or in the cloud?',
    answer:
      "Both options are supported. The managed AI option (Sortio-hosted or BYOK) is faster and more accurate. The local-only option runs through Ollama with a local LLM (Llama, Mistral) so file content never leaves your machine. For sensitive PDFs (legal, medical, financial), the local option is the right pick.",
  },
  {
    question: 'Can I keep my Hazel rules and add Sortio for the PDF cases that break?',
    answer:
      "Yes. Many users run both. Hazel handles the patterns that have always worked, and Sortio takes the PDFs that Hazel cannot route reliably. The Sortio + Hazel workflow guide walks through how to set up a staging folder that catches PDFs Hazel cannot match, then hands them to Sortio.",
  },
];

const HazelContentMatchingBlogPage = () => {
  const breadcrumbItems = [
    { label: 'Blog', href: null },
    { label: 'Hazel content matching PDFs', href: null },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.getsortio.com/' },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://www.getsortio.com/blog/hazel-content-matching-pdfs' },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Hazel content matching PDFs',
        item: CANONICAL,
      },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Why Hazel Content Matching Breaks on Real-World PDFs (and What to Do Instead)',
    description:
      'Hazel content matching is brittle on real-world bank statements, invoices, and scanned PDFs. Here is why, and the workflow that actually solves it.',
    url: CANONICAL,
    datePublished: PUBLISHED,
    dateModified: PUBLISHED,
    about: {
      '@type': 'SoftwareApplication',
      name: 'Sortio',
      url: 'https://www.getsortio.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'CMG Labs LLC',
      url: 'https://www.getsortio.com',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  return (
    <div className="min-h-screen bg-black text-apple-100">
      <SEOHead
        title="Why Hazel Content Matching Breaks on Real-World PDFs (and the Fix)"
        description="Hazel content matching is brittle on bank statements, invoices, and scanned PDFs. Here is why the regex-on-OCR approach fails and how AI-powered file sorting handles the same cases."
        canonical={CANONICAL}
        schema={[breadcrumbSchema, articleSchema, faqSchema]}
        keywords="hazel content matching, hazel pdf rules, hazel pdf content matching not working, hazel ocr, hazel alternative pdf, ai file organizer pdf"
        lastModified={PUBLISHED}
      />

      <div className="container mx-auto px-6 max-w-3xl py-16 lg:py-24">
        <Breadcrumbs items={breadcrumbItems} />

        <Link
          to="/compare"
          className="inline-flex items-center text-apple-300 hover:text-sortio-primary mb-8 transition-colors group"
        >
          <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
          <span>All comparisons</span>
        </Link>

        <header className="mb-10">
          <span className="inline-block px-3 py-1 bg-sortio-primary/10 text-sortio-primary text-xs font-medium rounded-lg uppercase tracking-wider mb-4">
            Blog
          </span>
          <h1 className="text-4xl lg:text-5xl font-semibold text-apple-50 mb-6 leading-tight">
            Why Hazel Content Matching Breaks on Real-World PDFs (and What to Do Instead)
          </h1>
          <div className="flex items-center text-apple-400 text-sm gap-4">
            <span className="flex items-center">
              <Clock className="mr-2" size={14} />
              Published {new Date(PUBLISHED).toLocaleDateString()}
            </span>
            <span>10 min read</span>
          </div>
        </header>

        <article className="prose-content space-y-6 text-apple-200 leading-relaxed">
          <p className="text-lg">
            If you have used Hazel for more than a year, you have probably hit the wall: a rule that reads
            an account number on one bank statement reliably, then silently fails on the next statement from the
            same bank. The rule looks correct. The PDF looks correct. The text is in there. And Hazel just shrugs.
          </p>

          <p>
            This is not user error. It is not a bad rule. It is the difference between how Hazel reads PDFs and
            how real-world PDFs actually behave. This post walks through the failure mode, why it happens, what
            workflows still hold up, and how AI-powered file sorting handles the cases where regex on OCR text
            falls apart.
          </p>

          <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-6 my-8 not-prose">
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-amber-400 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <h3 className="font-medium text-apple-50 mb-2">The short version</h3>
                <p className="text-apple-300 text-sm leading-relaxed">
                  Hazel matches text exposed by the PDF&apos;s OCR layer using regex. When the OCR layer is
                  inconsistent (different scanner, different software, different version of the same bank&apos;s
                  template), the regex misses. Tools that read PDF content semantically, not by regex, are robust
                  to those differences. The Hazel-plus-Sortio workflow keeps the rules that work and hands the
                  brittle cases to AI.
                </p>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            How Hazel content matching actually works
          </h2>

          <p>
            When you write a Hazel rule that says "if the file contents contain Account Number 0123," Hazel does
            three things behind the scenes. First, it asks the PDF for its embedded text layer. Second, it runs
            your match pattern against that text. Third, if the match succeeds, it fires the action.
          </p>

          <p>
            None of those steps include reading the visual page. Hazel does not OCR the PDF itself. It reads
            whatever text the PDF claims to have. For digitally generated PDFs (statements produced by online
            banking, invoices generated by accounting software, exports from tax preparation tools) that text
            layer is usually correct, and Hazel content matching works.
          </p>

          <p>
            For scanned PDFs, the story is different. The text layer is whatever the OCR engine produced when the
            scan was processed. If the scan came out of Preview&apos;s Quick Action, you get one OCR result. If
            the same document went through ScanSnap, you get a slightly different result. If your bank changed
            its statement template between Q2 and Q3, the document looks the same to a human but the OCR layer
            extracts subtly different text, and your regex misses.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            The three real-world failure modes
          </h2>

          <h3 className="text-xl font-medium text-apple-50 mt-8 mb-3">
            1. OCR noise (whitespace, ligatures, punctuation)
          </h3>
          <p>
            The text Hazel sees might be "Account Number: 0123-4567" on one statement and "Account Number:
            0123  4567" with two spaces on another. Or "Account No." instead of "Account Number." Or "Acct #"
            because the template changed. A regex written against the first form fails on the second. You can
            widen the regex with optional whitespace and alternation, but you cannot anticipate every variant a
            bank&apos;s template designer might introduce next quarter.
          </p>

          <h3 className="text-xl font-medium text-apple-50 mt-8 mb-3">
            2. Layout-dependent extraction order
          </h3>
          <p>
            PDFs do not store text in reading order. They store text in placement order. Two-column statements,
            tabular invoices, and side-by-side metadata blocks come back from the text-extraction call in an
            order that depends on the PDF producer. Sometimes the account number is adjacent to the customer
            name in the extracted stream; sometimes there is a page header between them. Regex that depends on
            proximity ("Account Number" within 50 characters of a number pattern) breaks unpredictably.
          </p>

          <h3 className="text-xl font-medium text-apple-50 mt-8 mb-3">
            3. Mixed-quality scans in a long archive
          </h3>
          <p>
            A real archive of three years of bank statements probably has scans from at least four sources:
            recent ones from online banking PDFs (clean OCR), older ones scanned at home (medium OCR), even
            older ones scanned at a copy shop years ago (rough OCR), and a few that were emailed as images and
            never properly OCR&apos;d. A single Hazel rule cannot handle all four; you end up writing four rules,
            each with its own quirks, and the failure modes become indistinguishable.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            The workarounds that mostly work
          </h2>

          <p>
            Heavy Hazel users have built a small ecosystem of workarounds for this. The most common is to insert
            an OCR-rebuilding step before the content-matching rule fires. OCRmyPDF, run as a shell command via
            Hazel&apos;s action, will produce a canonical text layer that is more consistent than whatever the
            original scanner produced. It works. It is also brittle, because OCRmyPDF has its own version
            differences and edge cases, and the shell-script-in-a-rule pattern is hard to debug when it breaks.
          </p>

          <p>
            The second workaround is to use Hazel only for routing by filename or extension, and to do the
            content extraction in a downstream script. AppleScript, JavaScript for Automation, or a Python
            script can pull out the data, normalize it, and then move the file. This is a real engineering
            project and most users do not want to maintain a shell-script library to keep their bank statements
            organized.
          </p>

          <p>
            The third workaround is to give up on content matching and route on filename instead. This works if
            you can rename PDFs at the point of arrival (for example, if your scanner names files using a fixed
            template). It does not work for downloaded statements, which usually arrive with the bank&apos;s
            generic name like "statement.pdf."
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Why AI sorting handles these cases differently
          </h2>

          <p>
            A language model does not need a regex. When Sortio reads a PDF, it asks an LLM to look at the
            extracted text and decide where the file should go. The LLM is robust to OCR noise in a way regex is
            not: "Account Number: 0123-4567" and "Acct # 0123 4567" and "A/C No 01234567" all read as the same
            thing to a model. The model also understands the document. It knows what a bank statement looks
            like, what an invoice looks like, what a tax form looks like, and routes accordingly.
          </p>

          <p>
            The trade-off is cost and speed. Every AI sort uses some of your AI allowance, and inference takes
            longer than regex. For routing 30 bank statements a month, this is invisible: a single sort takes
            seconds and barely touches your allowance. For routing 30,000 files where the routing logic could be
            expressed in a regex, AI is overkill and rules are still the right tool.
          </p>

          <p>
            This is why Sortio has both. AI Sort takes a prompt and runs an LLM per file. AI Rule Builder turns
            a plain-English description into a deterministic rule that does not use your AI allowance at all. You write
            the rule once and it runs forever. The mental model: use rules for the clean cases that have stable
            patterns, and use AI sort for the messy ones where Hazel content matching has been breaking.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            What the migration looks like in practice
          </h2>

          <p>
            Say you have a Hazel rule that has been breaking on bank statements from one of your accounts. The
            rule is supposed to read the account number, identify the bank, and route the PDF to the right
            account folder. Some statements work, some do not, and you cannot tell why.
          </p>

          <p>
            With Sortio, you delete that rule and replace it with a sort prompt. You point Sortio at the folder
            where bank statements land, and write: "Sort these bank statements into folders by account. The
            folder names are Personal Checking, Personal Savings, Joint Checking, and Business. The account
            number is on the first page of each statement." Sortio reads each PDF, identifies the account, and
            routes accordingly. Statements that previously broke the Hazel rule because of OCR drift work
            because the LLM is reading the document, not pattern-matching on noisy text.
          </p>

          <p>
            For the rules that already worked in Hazel (move screenshots to a screenshots folder, archive
            installers, route downloaded receipts by extension), there is no reason to migrate. Leave them in
            Hazel. The Sortio + Hazel workflow guide explains how to set up a staging folder that catches the
            files Hazel could not confidently route and hands them to Sortio for AI sorting.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            What stays in Hazel
          </h2>

          <p>
            Hazel is still the best tool for a specific set of patterns. Anything that depends purely on
            metadata (extension, name pattern, date, source app, size, location) is rule-shaped and belongs in
            Hazel. AppleScript and shell-script hooks are deep and mature, and if you have built a workflow
            around them, do not throw it away. The same applies to color-tag based workflows, Finder comment
            workflows, and date-based archival.
          </p>

          <p>
            The Hazel rules that break, in our experience and in the public threads from MacPowerUsers and
            Reddit, are almost always the content-matching ones. Those are the ones to migrate. The rest can
            keep running.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            FAQ
          </h2>

          <div className="space-y-4 not-prose">
            {FAQ.map((f, i) => (
              <div key={i} className="bg-apple-700/20 rounded-xl p-6">
                <h3 className="font-medium text-apple-50 mb-3">{f.question}</h3>
                <p className="text-apple-300 leading-relaxed">{f.answer}</p>
              </div>
            ))}
          </div>
        </article>

        {/* Internal links / next steps */}
        <section className="mt-16 pt-8 border-t border-apple-700/30">
          <h2 className="text-xl font-semibold text-apple-50 mb-4">Keep reading</h2>
          <div className="grid gap-3">
            <Link
              to="/compare/hazel-alternative-mac"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Hazel alternatives for Mac (the full roundup)
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
            <Link
              to="/guides/migrate-from-hazel-to-sortio"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Migrate from Hazel to Sortio (rule translator)
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
            <Link
              to="/guides/sortio-plus-hazel-workflow"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Sortio + Hazel: use both side by side
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
            <Link
              to="/blog/automatically-rename-pdfs-by-content"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Automatically rename PDFs by content (invoices, receipts, statements)
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
          </div>
        </section>

        <section className="mt-12">
          <div className="bg-gradient-to-r from-sortio-primary/10 to-sortio-secondary/10 rounded-2xl p-8 border border-sortio-primary/20">
            <div className="flex items-center mb-4">
              <Sparkles className="text-sortio-primary mr-3" size={24} />
              <h2 className="text-2xl font-semibold text-apple-50">Try Sortio on a problem folder</h2>
            </div>
            <p className="text-apple-300 mb-6 leading-relaxed">
              The free tier includes a one-time AI trial allowance, enough to run Sortio on a folder of PDFs that Hazel has
              been failing on and see what changes. No credit card.
            </p>
            <Link
              to="/download"
              className="inline-flex items-center px-6 py-3 bg-sortio-primary hover:bg-sortio-primary/90 text-white font-medium rounded-xl transition-colors duration-200"
            >
              Download Sortio
              <ArrowRight className="ml-2" size={16} />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default HazelContentMatchingBlogPage;
